/**
 * Run view app switcher — the full-page "Your applications" landing, opened
 * from the Run top bar's Apps button.
 *
 * Deliberately NOT the builder's ProjectsModal: that dialog is shared by the
 * left rail / Builder hub / Workbench and stays exactly as it is. This picker
 * serves only the Run view, so an end user switching apps sees the same
 * landing the downloaded runtime shows (runtime/client HomePage), built from
 * the same GET /workbench/projects list.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import type { ProjectData } from '@/types'
import { useProjectStore } from '@/store/projectStore'
import { useAuth } from '@/auth/AuthContext'
import { loadUsage } from './runUsage'
import logoSmall from '@/assets/logo-small.png'
import logoLarge from '@/assets/logo-large.png'

type SortKey = 'frequent' | 'recent' | 'built' | 'modified'
const SORTS: { key: SortKey; label: string; icon: string }[] = [
  { key: 'frequent', label: 'Frequently used', icon: 'trending_up' },
  { key: 'recent', label: 'Recently used', icon: 'history' },
  { key: 'built', label: 'Recently built', icon: 'construction' },
  { key: 'modified', label: 'Recently modified', icon: 'edit_calendar' },
]

/**
 * Who provides an application, relative to the signed-in account:
 *  mine — built by this account (private to them unless shared on)
 *  org  — built by a colleague in the same organization
 *  ext  — built by another organization and shared with this one
 */
type Origin = 'mine' | 'org' | 'ext'
const ORIGINS: Record<Origin, { icon: string; label: string; tint: string; bg: string }> = {
  mine: { icon: 'lock', label: 'Private · built by you', tint: '#0f766e', bg: '#e6f6f2' },
  org: { icon: 'business', label: 'Built in your organization', tint: '#0050ab', bg: '#e8f0fc' },
  ext: { icon: 'public', label: 'Provided by another organization', tint: '#9a3412', bg: '#fdeee3' },
}
const originOf = (p: ProjectData, tenantId?: string): Origin => {
  const a = p.access
  if (!a || a.is_owner) return 'mine'
  if (a.owner_tenant_id && tenantId && a.owner_tenant_id !== tenantId) return 'ext'
  return 'org'
}
/** "Name · Organization" — the provider line shown on a card. */
const providerOf = (p: ProjectData, origin: Origin): string => {
  const a = p.access
  if (origin === 'mine') return 'You'
  const who = a?.owner_name || ''
  const org = origin === 'ext' ? (a?.owner_tenant_name || '') : ''
  return [who, org].filter(Boolean).join(' · ') || 'Unknown provider'
}
const ts = (iso?: string) => { const t = iso ? Date.parse(iso) : NaN; return Number.isNaN(t) ? 0 : t }

const FONT = "'Manrope', system-ui, sans-serif"
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
.rap-card{transition:box-shadow .15s,transform .15s,border-color .15s}
.rap-card:hover{box-shadow:0 12px 28px rgba(15,23,42,.12);transform:translateY(-2px);border-color:var(--rap-accent,#a5b4fc)!important}
.rap-card:focus-visible,.rap-btn:focus-visible,.rap-close:focus-visible{outline:3px solid #0061D0;outline-offset:2px}
@media (max-width:640px){.rap-main{padding:24px 16px!important}.rap-head{padding-left:16px!important;padding-right:16px!important}.rap-h1{font-size:24px!important}.rap-logo-l{display:none!important}.rap-logo-s{display:block!important}}
`

const canRun = (p: ProjectData) => !!p.application.generated || (p.navigation?.length ?? 0) > 0
/** An application the caller could actually open in the Run view. */
export const canRunApp = canRun

export default function RunAppPicker({ currentProject, onClose }: {
  /** Folder name of the app being run now (highlighted, "Current"). */
  currentProject: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects, fetchProjects } = useProjectStore()
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortKey>('frequent')
  const usage = useMemo(() => loadUsage(), [])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.resolve(fetchProjects())
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load applications') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [fetchProjects])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  const tenant = user?.tenant_id || '_'
  const q = query.trim().toLowerCase()
  const list = useMemo(
    () => projects
      .filter(canRun)
      .filter((p) => !q
        || p.application.title.toLowerCase().includes(q)
        || (p.application.folder_name || '').toLowerCase().includes(q))
      .sort((a, b) => {
        const A = a.application, B = b.application
        const byName = A.title.localeCompare(B.title, undefined, { sensitivity: 'base' })
        const ua = usage[A.folder_name], ub = usage[B.folder_name]
        let d = 0
        if (sort === 'frequent') d = (ub?.opens || 0) - (ua?.opens || 0) || (ub?.last || 0) - (ua?.last || 0)
        else if (sort === 'recent') d = (ub?.last || 0) - (ua?.last || 0)
        else if (sort === 'built') d = ts(B.created_at) - ts(A.created_at)
        else d = ts(B.updated_at) - ts(A.updated_at)
        return d || byName
      }),
    [projects, q, sort, usage],
  )
  const current = !q ? list.find((p) => p.application.folder_name === currentProject) : undefined
  const rest = current ? list.filter((p) => p !== current) : list

  const runApp = (p: ProjectData) => {
    onClose()
    navigate(`/run/${encodeURIComponent(tenant)}/${encodeURIComponent(p.application.folder_name)}`)
  }
  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label="Your applications"
      style={{ position: 'fixed', inset: 0, zIndex: 9500, overflowY: 'auto', background: '#f5f6fa', fontFamily: FONT, color: '#0f172a' }}
    >
      <style>{CSS}</style>
      <header className="rap-head" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        padding: '10px 40px', background: '#fff', borderBottom: '1px solid #e5e7ef',
      }}>
        <img src={logoLarge} alt="Twasta.ai" className="rap-logo-l" style={{ height: 28, objectFit: 'contain' }} />
        <img src={logoSmall} alt="Twasta.ai" className="rap-logo-s" style={{ width: 32, height: 32, display: 'none' }} />
        <button
          type="button" onClick={onClose} className="rap-close"
          style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 16px', border: '1px solid #d5d9e6', borderRadius: 10, background: '#fff', color: '#334155', font: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
        >
          <span className="material-icons" aria-hidden="true" style={{ fontSize: 18 }}>close</span>
          Close
        </button>
      </header>

      <main className="rap-main" style={{ maxWidth: 1120, margin: '0 auto', boxSizing: 'border-box', padding: '48px 40px', display: 'flex', flexDirection: 'column', gap: 32 }}>
        <section style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 560 }}>
            <h1 className="rap-h1" style={{ margin: 0, fontSize: 28, lineHeight: 1.15, letterSpacing: '-0.03em', fontWeight: 800 }}>Your applications</h1>
            <p style={{ margin: 0, fontSize: 17, lineHeight: 1.5, color: error ? '#b91c1c' : '#475569' }}>
              {loading ? 'Loading…' : error ?? 'Switch to another application you have access to.'}
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #d5d9e6', borderRadius: 12, padding: '0 14px', minHeight: 48, width: 320, maxWidth: '100%', boxSizing: 'border-box' }}>
            <span className="material-icons" aria-hidden="true" style={{ fontSize: 20, color: '#64748b' }}>search</span>
            <input
              type="search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search applications" aria-label="Search applications"
              style={{ border: 0, outline: 0, background: 'transparent', font: 'inherit', fontSize: 15, flex: 1, minWidth: 0, color: '#0f172a' }}
            />
          </label>
        </section>

        {loading && (
          <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
            {[0, 1, 2].map((i) => <div key={i} style={{ height: 128, borderRadius: 16, background: '#e8eaf3' }} />)}
          </div>
        )}

        {current && (
          <section aria-label="Current application" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Label>Current</Label>
            <AppCard project={current} hero current tenantId={user?.tenant_id} onRun={runApp} />
          </section>
        )}

        {!loading && (
          <section aria-label="All applications" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <Label>{current ? 'More applications' : 'All applications'} · {rest.length}</Label>
            <SortMenu value={sort} onChange={setSort} />
            </div>
            {rest.length > 0 ? (
              <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
                {rest.map((p) => (
                  <AppCard key={`${p.application.id}/${p.application.folder_name}`} project={p} tenantId={user?.tenant_id} onRun={runApp} />
                ))}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 15, color: '#475569' }}>
                {q ? `No applications match “${query}”.` : 'No other applications are available to you.'}
              </p>
            )}
          </section>
        )}
      </main>
    </div>,
    document.body,
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#475569' }}>
      {children}
    </h2>
  )
}

function AppCard({ project, hero = false, current = false, tenantId, onRun }: {
  project: ProjectData
  hero?: boolean
  current?: boolean
  tenantId?: string
  onRun: (p: ProjectData) => void
}) {
  const app = project.application
  const origin = originOf(project, tenantId)
  const o = ORIGINS[origin]
  const provider = providerOf(project, origin)
  const accent = app.theme_color || '#0061D0'
  const runnable = canRun(project)
  const icon = app.icon_image_data
    ? <img src={app.icon_image_data} alt="" style={{ width: hero ? 38 : 26, height: hero ? 38 : 26, objectFit: 'contain' }} />
    : <span className="material-icons" aria-hidden="true" style={{ fontSize: hero ? 38 : 26, color: '#fff' }}>{app.icon || 'apps'}</span>
  const open = () => { if (runnable) onRun(project) }
  const meta = current ? 'Open now' : 'Open application'

  return (
    <div
      className="rap-card"
      style={{
        display: 'flex', flexDirection: hero ? 'row' : 'column', flexWrap: 'wrap', alignItems: hero ? 'center' : 'stretch', gap: hero ? 28 : 16,
        padding: hero ? 28 : 22, borderRadius: hero ? 20 : 16,
        background: hero ? `linear-gradient(135deg, #0050ab 0%, #007a5e 100%)` : '#fff',
        border: hero ? '1px solid #0050ab' : '1px solid #e5e7ef',
        ['--rap-accent' as string]: accent,
      }}
    >
      <button
        type="button" onClick={open} disabled={!runnable} className="rap-btn"
        style={{
          display: 'flex', flex: hero ? '1 1 280px' : undefined, flexDirection: hero ? 'row' : 'column', alignItems: hero ? 'center' : 'flex-start', gap: hero ? 28 : 18,
          padding: 0, border: 0, background: 'transparent', font: 'inherit', textAlign: 'left', color: 'inherit',
          cursor: runnable ? 'pointer' : 'default', minWidth: 0,
        }}
      >
        <span style={{
          width: hero ? 72 : 48, height: hero ? 72 : 48, borderRadius: hero ? 18 : 14, flexShrink: 0,
          background: hero ? 'rgba(255,255,255,.14)' : accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <strong style={{ fontSize: hero ? 28 : 18, fontWeight: 800, letterSpacing: hero ? '-0.02em' : undefined, color: hero ? '#fff' : '#0f172a' }}>{app.title}</strong>
          <span
            title={`${o.label} — ${provider}`}
            style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontSize: 14, color: hero ? 'rgba(255,255,255,.85)' : '#475569' }}
          >
            <span
              className="material-icons" aria-label={o.label} role="img"
              style={{
                fontSize: 15, flexShrink: 0, width: 24, height: 24, borderRadius: 7,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: hero ? 'rgba(255,255,255,.18)' : o.bg, color: hero ? '#fff' : o.tint,
              }}
            >{o.icon}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{provider}</span>
          </span>
          {hero ? (
            <span style={{ alignSelf: 'flex-start', marginTop: 4, fontSize: 13, fontWeight: 800, background: '#FFC93C', color: '#06264f', padding: '3px 12px', borderRadius: 99 }}>{meta}</span>
          ) : (
            <span style={{ fontSize: 14, color: '#475569' }}>{meta}</span>
          )}
        </span>
      </button>
    </div>
  )
}

/**
 * Compact sort menu. A native <select> cannot show an icon per option and
 * stretches to its longest label, so this is a small button + listbox.
 */
function SortMenu({ value, onChange }: { value: SortKey; onChange: (k: SortKey) => void }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const cur = SORTS.find((o) => o.key === value) ?? SORTS[0]

  useEffect(() => {
    if (!open) return undefined
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    // Capture phase: close this menu before the picker's own Escape handler closes the page.
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('mousedown', away)
    window.addEventListener('keydown', esc, true)
    return () => { document.removeEventListener('mousedown', away); window.removeEventListener('keydown', esc, true) }
  }, [open])

  return (
    <div ref={box} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button" className="rap-btn" aria-label="Sort applications" aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '0 8px 0 10px', border: '1px solid #d5d9e6', borderRadius: 10, background: '#fff', font: 'inherit', fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        <span className="material-icons" aria-hidden="true" style={{ fontSize: 17, color: '#0061D0' }}>{cur.icon}</span>
        {cur.label}
        <span className="material-icons" aria-hidden="true" style={{ fontSize: 18, color: '#64748b' }}>expand_more</span>
      </button>
      {open && (
        <ul
          role="listbox" aria-label="Sort applications"
          style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 5, margin: 0, padding: 4, listStyle: 'none', minWidth: 190, background: '#fff', border: '1px solid #e5e7ef', borderRadius: 12, boxShadow: '0 12px 28px rgba(15,23,42,.14)' }}
        >
          {SORTS.map((o) => (
            <li key={o.key} role="option" aria-selected={o.key === value}>
              <button
                type="button" className="rap-btn"
                onClick={() => { onChange(o.key); setOpen(false) }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 38, padding: '0 10px', border: 0, borderRadius: 8, background: o.key === value ? '#eef4fd' : 'transparent', font: 'inherit', fontSize: 14, fontWeight: o.key === value ? 700 : 500, color: '#0f172a', cursor: 'pointer', textAlign: 'left' }}
              >
                <span className="material-icons" aria-hidden="true" style={{ fontSize: 18, color: o.key === value ? '#0061D0' : '#64748b' }}>{o.icon}</span>
                <span style={{ flex: 1 }}>{o.label}</span>
                {o.key === value && <span className="material-icons" aria-hidden="true" style={{ fontSize: 18, color: '#0061D0' }}>check</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
